import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { filterPrimaryDocuments } from '@repo/shared/lib';
import { DocumentExtractionStatus, DocumentSide } from '@repo/shared/types';
import { Badge } from '@repo/ui/badge';
import { Button } from '@repo/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/dialog';
import { cn } from '@repo/ui/lib/utils';
import { PDFViewerLazy } from '@repo/ui/pdf-viewer/pdf-viewer-lazy';
import { ScrollArea } from '@repo/ui/scroll-area';
import { FileText, ImageIcon, ScanLine } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { WizardReport } from '@/lib/report';
import { trpc } from '@/providers/trpc';
import { DocumentScanner } from './document-scanner';
import { ExtractedData } from './extracted-data';

type ExtractionModalProps = {
  open: boolean;
  report: WizardReport;
  /** Called after accept — typically advance to the next wizard step */
  onAccepted: () => void;
  /** Called after reject — typically stay on upload */
  onRejected: () => void;
};

/**
 * Full-screen dialog after the user continues from the upload step.
 * Enqueues extraction when opened if documents are still pending, then shows
 * preview + extracted fields (skeleton while extraction runs).
 */
export function ExtractionModal({ open, report, onAccepted, onRejected }: ExtractionModalProps) {
  useLocale();

  const utils = trpc.useUtils();

  const primaryDocs = filterPrimaryDocuments(report?.documents ?? []);

  const [activeDocId, setActiveDocId] = useState<string>(() => primaryDocs?.[0]?._id ?? '');

  const centerScrollRef = useRef<HTMLDivElement>(null);

  const { mutate: startExtraction } = trpc.report.startDocumentExtraction.useMutation({
    onSuccess: () => {
      void utils.report.getReport.invalidate({ reportId: report._id });
    },
    onError: () => {
      toast.error(t('reports.failedToStartExtraction'), {
        get description() {
          return t('reports.pleaseTryAgain');
        },
      });
    },
  });

  const [scanProgress, setScanProgress] = useState(0);

  useEffect(() => {
    if (!open) return;
    startExtraction({ reportId: report._id });
  }, [open, report._id, startExtraction]);

  // Live report to track extraction progress
  const { data: liveReport } = trpc.report.getReport.useQuery(
    { reportId: report._id },
    {
      initialData: report as never,
      enabled: open,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        if (!data) return false;
        const primary = filterPrimaryDocuments(data.documents ?? []);
        const extracting = primary.some(
          (d) =>
            d.extractionStatus === DocumentExtractionStatus.PENDING ||
            d.extractionStatus === DocumentExtractionStatus.PROCESSING,
        );
        return extracting ? 2500 : false;
      },
    },
  );

  const livePrimary = filterPrimaryDocuments(liveReport.documents ?? []);

  useEffect(() => {
    if (!open || !livePrimary?.length) return;
    setActiveDocId((id) =>
      id && livePrimary.some((d) => d._id === id) ? id : livePrimary[0]!._id,
    );
  }, [open, livePrimary]);

  const isExtracting = (livePrimary ?? []).some(
    (d) =>
      d.extractionStatus === DocumentExtractionStatus.PENDING ||
      d.extractionStatus === DocumentExtractionStatus.PROCESSING,
  );

  const hasFailedExtraction = (livePrimary ?? []).some(
    (d) => d.extractionStatus === DocumentExtractionStatus.FAILED,
  );

  // Simulate scan progress during validation (for scanning animation)
  useEffect(() => {
    if (!isExtracting || hasFailedExtraction) return;
    const start = Date.now();
    const duration = 8000;
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      setScanProgress(Math.min(90, (elapsed / duration) * 90));
    }, 100);
    return () => clearInterval(interval);
  }, [isExtracting, hasFailedExtraction]);

  const displayProgress = isExtracting && !hasFailedExtraction ? scanProgress : 0;

  // ── Document download URL ──────────────────────────────────────────────────
  const { data: downloadUrl } = trpc.media.getDownloadUrl.useQuery(
    { storageKey: primaryDocs?.find((pd) => pd._id === activeDocId)?.storageKey || '' },
    {
      enabled: !!primaryDocs?.find((pd) => pd._id === activeDocId)?.storageKey,
    },
  );

  // ── Accept extraction ──────────────────────────────────────────────────────
  const { mutate: accept, isPending: isAccepting } = trpc.report.acceptExtraction.useMutation({
    onSuccess: () => {
      toast.success(t('reports.extractionAccepted'));
      onAccepted();
    },
    onError: () => {
      toast.error(t('reports.failedToAcceptExtraction'), {
        get description() {
          return t('reports.pleaseTryAgain');
        },
      });
    },
  });

  // ── Reject extraction ──────────────────────────────────────────────────────
  const { mutate: reject, isPending: isRejecting } = trpc.report.rejectExtraction.useMutation({
    onSuccess: () => {
      toast.info(t('reports.documentsRemovedPleaseUploadNewFiles'));
      onRejected();
    },
    onError: () => {
      toast.error(t('reports.failedToRejectExtraction'), {
        get description() {
          return t('reports.pleaseTryAgain');
        },
      });
    },
  });

  const activeDoc = livePrimary?.find((d) => d._id === activeDocId);

  const isBusy = isAccepting || isRejecting;

  return (
    <Dialog open={open}>
      <DialogContent className="flex h-[90vh] w-full max-w-6xl! flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl!">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            {isExtracting ? t('reports.extractingDocumentData') : t('reports.reviewExtractedData')}
          </DialogTitle>
        </DialogHeader>

        <div className="relative flex flex-1 overflow-hidden">
          {/* ── Left panel: file list ── */}
          <div className="flex w-52 shrink-0 flex-col overflow-hidden border-e">
            <p className="shrink-0 px-3 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {t('reports.documents')}{' '}
            </p>
            <ScrollArea className="flex-1">
              <div className="flex flex-col gap-1 p-2">
                {livePrimary?.map((doc) => (
                  <button
                    className={cn(
                      'flex w-full flex-col items-start gap-1 rounded-md px-2.5 py-2 text-start text-xs transition-colors',
                      doc._id === activeDocId
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                    key={doc._id}
                    onClick={() => setActiveDocId(doc._id)}
                    type="button"
                  >
                    <div className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      <span className="max-w-[110px] truncate font-medium">
                        {formatDocType(doc.documentType)}
                      </span>
                    </div>
                    {doc.side && doc.side !== DocumentSide.FRONT ? null : doc.side ? (
                      <Badge className="h-4 text-[9px]" variant="outline">
                        {doc.side}
                      </Badge>
                    ) : null}
                    <ExtractionStatusBadge status={doc.extractionStatus} />
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* ── Center panel: document preview ── */}
          <div className="flex flex-1 flex-col overflow-hidden border-e">
            <p className="shrink-0 border-b px-4 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {t('reports.preview')}{' '}
            </p>
            <div
              className="relative flex-1 overflow-y-auto overflow-x-hidden rounded-lg border bg-muted/30"
              ref={centerScrollRef}
            >
              <DocumentScanner
                isScanning={isExtracting && !hasFailedExtraction}
                progress={displayProgress}
              />
              {downloadUrl?.url ? (
                isImageUrl(downloadUrl?.url) ? (
                  <img
                    alt={t('reports.documentPreview2')}
                    className="h-auto w-full max-w-full rounded border"
                    src={downloadUrl?.url}
                  />
                ) : (
                  <PDFViewerLazy
                    className="min-h-full"
                    data={downloadUrl?.url}
                    scrollParentRef={centerScrollRef}
                  />
                )
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <ImageIcon className="h-10 w-10 text-muted-foreground/30" />
                  <p className="text-muted-foreground text-sm">
                    {t('reports.documentPreviewWillAppearHereOnceTheDownloadUrl')}{' '}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Right panel: extracted data ── */}
          <div className="flex w-72 shrink-0 flex-col overflow-hidden">
            <p className="shrink-0 border-b px-4 py-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {t('reports.extractedFields')}{' '}
            </p>
            <ScrollArea className="h-10/12 flex-1">
              <div className="mb-4 flex flex-col gap-3 p-4">
                <ExtractedData
                  extractedData={activeDoc?.extractedData}
                  isExtracting={isExtracting}
                  reportType={report.reportType}
                />
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="flex shrink-0 items-center justify-end border-t sm:justify-end">
          <Button
            disabled={isBusy || isExtracting || hasFailedExtraction}
            loading={isAccepting}
            onClick={() => accept({ reportId: report._id })}
          >
            {t('reports.acceptContinue')}{' '}
          </Button>
          <Button
            disabled={isBusy || isExtracting}
            loading={isRejecting}
            onClick={() => reject({ reportId: report._id })}
            variant="destructive"
          >
            {t('reports.rejectReUpload')}{' '}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDocType(type: string) {
  return type
    .split('_')
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function isImageUrl(url: string) {
  return /\.(jpg|jpeg|png|webp|gif|bmp)(\?|$)/i.test(url);
}

function ExtractionStatusBadge({ status }: { status: string }) {
  useLocale();

  const map: Record<string, { label: string; className: string }> = {
    PENDING: {
      get label() {
        return t('reports.pending');
      },
      className: 'text-muted-foreground',
    },
    PROCESSING: {
      get label() {
        return t('reports.processing');
      },
      className: 'text-yellow-600',
    },
    COMPLETED: {
      get label() {
        return t('reports.done');
      },
      className: 'text-green-600',
    },
    FAILED: {
      get label() {
        return t('reports.failed');
      },
      className: 'text-destructive',
    },
  };
  const entry = map[status] ?? { label: status, className: 'text-muted-foreground' };
  return <span className={cn('font-medium text-[9px]', entry.className)}>{entry.label}</span>;
}
